import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import { Dayjs } from 'dayjs'
import { toast } from 'react-toastify'

import { useTableData } from '@/hooks/useTableData'
import { useFilterModal } from '@/hooks/useCustomFilter'
import { useLoading } from '@/hooks/useLoading'
import { useSearchParams } from '@/hooks/useSearchParams'

import CustomButton from '@/components/CustomButton'
import CustomGraphy from '@/components/CustomGraphy'
import CustomTable from '@/components/CustomTable'
import CustomView from '@/components/CustomView'
import FormDatePicker from '@/components/FormDatePicker'
import FormSelect from '@/components/FormSelect'
import PageTitle from '@/components/PageTitle'

import {
  convertToUtcDayjs,
  formatDayjsToISO,
  getDateHours,
  prepareFilters
} from '@/utils/helpers'
import { SUCCESS_STATUS } from '@/utils/CONSTANTS'
import { integrationService } from '@/services/Integration'
import { IntegrationTypeOptions } from '@/context/AssetLocationContext'
import { EIntegrationTypes } from '@/types/enums/integrations'
import FormChipsInput from '@/components/FormChipsInput'
import FormInput from '@/components/FormInput'
import ModalIntegration from '../AssetLocation/components/ModalIntegration'
import { assetLocationServiceV2 } from '@/services/AssetLocation/v2/assetLocation'
import { useLocation } from 'react-router-dom'
import { useAssetLocation } from '@/hooks/useAssetLocation'

const initialFilterState = {
  tipoIntegracao: '',
  status: '',
  dataInicioCadastro: null,
  detalheEnvio: ''
}

export default function Integrations() {
  const {
    SET_CURRENT_PAGE,
    SET_PER_PAGE,
    SET_SORT_ASCENDING,
    SET_SORT_FIELD,
    SORT_FIELD,
    SORT_ASCENDING,
    CURRENTE_PAGE,
    PER_PAGE
  } = useTableData()

  const location = useLocation()
  const { handleClose } = useFilterModal()
  const { LOADING, SET_LOADING } = useLoading()
  const [data, setData] = useState<any>({})

  const columnsArr = [
    'Status',
    'Data/Hora',
    'Tipo Integração',
    'Detalhe Envio',
    'Observação'
  ]
  const [triggerFetch, setTriggerFetch] = useState(0)
  const [isFirstRender, setIsFirstRender] = useState(true)
  const { searchParams, updateSearchParams } = useSearchParams()

  const [integrationValues, setIntegrationValues] = useState<{
    tipoIntegracao: { value: number; label: string } | null
    placas: string[]
  }>({
    tipoIntegracao: null,
    placas: []
  })

  const [openModalIntegration, setOpenModalIntegration] = useState(false)

  const { CAN_EXECUTE_INTEGRACOES } = useAssetLocation()

  const resetPage = () => {
    setFieldValue('page', 1)
    SET_CURRENT_PAGE(1)
  }

  const updateTablePagination = () => {
    if (searchParams?.pageSize) SET_PER_PAGE(Number(searchParams.pageSize))
    if (searchParams?.sortField) SET_SORT_FIELD(searchParams.sortField)
    if (searchParams?.sortAscending !== undefined)
      SET_SORT_ASCENDING(searchParams.sortAscending)
    searchParams?.page !== undefined
      ? SET_CURRENT_PAGE(Number(searchParams.page))
      : SET_CURRENT_PAGE(1)
  }

  const applyURLParams = async () => {
    let formData = { ...initialFilterState, ...searchParams }

    setValues(formData)
    if (!searchParams?.sortField) {
      SET_SORT_FIELD('dataCadastro')
      SET_SORT_ASCENDING(false)
    } else {
      updateTablePagination()
    }
    setTriggerFetch((prev) => prev + 1)
  }

  const {
    values,
    setValues,
    handleSubmit,
    handleChange,
    setFieldValue,
    resetForm
  } = useFormik({
    initialValues: initialFilterState,
    enableReinitialize: true,
    onSubmit: (values) => {
      resetPage()
      applyFilters(values)
    }
  })

  const recoveryIntegration = async (params?: any) => {
    try {
      SET_LOADING(true)

      const payload = {
        Page: CURRENTE_PAGE,
        PageSize: PER_PAGE,
        SortField: SORT_FIELD,
        SortAscending: SORT_ASCENDING,
        ...params
      }

      const res: any = await integrationService.getIntegrations(payload)
      const integrations = res.data.data
      setData(integrations)
      return integrations
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      SET_LOADING(false)
    }
  }

  const applyFilters = async (values: any) => {
    try {
      const params = {
        ...values,
        tipoIntegracao: values.tipoIntegracao?.value || ''
      }

      params.page = 1
      updateSearchParams(params, true, true)
    } finally {
      handleClose()
    }
  }

  const formModalIntegration = () => {
    return (
      <CustomView mt="15px" justifyContent="center" gap={4}>
        <CustomView gap="10px">
          <FormSelect
            withBorder
            label="Tipo Integração"
            id="tipoIntegracao"
            name="tipoIntegracao"
            value={integrationValues.tipoIntegracao}
            onChange={(name: string, value: IntegrationTypeOptions) => {
              setIntegrationValues((prev) => ({
                ...prev,
                [name]: value
              }))
            }}
            options={[{ value: 1, label: 'Integração Veículo' }]}
            optionLabel="label"
          />
        </CustomView>
        <CustomView maxWidth="100%" gap="10px">
          {integrationValues.tipoIntegracao?.value ===
            EIntegrationTypes.IntegracaoVeiculo && (
            <FormChipsInput
              id="placas"
              name="placas"
              labelName="Placas"
              placeholder="Placas"
              value={integrationValues.placas}
              onChange={(name: string, value: string[]) =>
                setIntegrationValues((prev) => ({
                  ...prev,
                  [name]: value
                }))
              }
            />
          )}
        </CustomView>
      </CustomView>
    )
  }

  const mountFilters = (values: any) => ({
    tipoIntegracao: values.tipoIntegracao,
    status: prepareFilters(values.status),
    dataInicioCadastro: formatDayjsToISO(values.dataInicioCadastro),
    detalheEnvio: values.detalheEnvio
  })

  async function resetFilters() {
    try {
      resetForm()
      resetPage()
      updateSearchParams(initialFilterState, true, true)
    } finally {
      handleClose()
    }
  }

  useEffect(() => {
    ;(async () => {
      if (isFirstRender) setIsFirstRender(false)
      applyURLParams()
    })()
  }, [location?.search])

  useEffect(() => {
    ;(async () => {
      if (isFirstRender) return
      await recoveryIntegration(mountFilters(values))
    })()
  }, [triggerFetch])

  const formFilter = () => {
    return (
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <CustomView width={1090} mt="15px" justifyContent="center">
          <CustomView flexDirection="row" gap={2}>
            <FormSelect
              withBorder
              label="Tipo Integração"
              id="tipoIntegracao"
              name="tipoIntegracao"
              value={values.tipoIntegracao}
              onChange={setFieldValue}
              options={[
                { value: 'IntegracaoVeiculo', label: 'Integração Veículo' }
              ]}
              optionLabel="label"
            />
            <FormSelect
              withBorder
              label="Status"
              id="status"
              name="status"
              value={values.status}
              onChange={setFieldValue}
              options={['Fila', 'Em processamento', 'Concluido', 'Erro']}
            />
            <FormInput
              id="DetalheEnvio"
              name="detalheEnvio"
              labelName="Placa"
              pr={{ md: '6px' }}
              value={values.detalheEnvio}
              onChange={handleChange}
              mask="WithoutMask"
              customStyle="withBorder"
            />

            <FormDatePicker
              id="dataInicioCadastro"
              name="dataInicioCadastro"
              label="Criado em"
              customStyle="withBorder"
              value={
                values.dataInicioCadastro
                  ? convertToUtcDayjs(values.dataInicioCadastro)
                  : null
              }
              onChange={(date: Dayjs) =>
                setFieldValue('dataInicioCadastro', date)
              }
            />
          </CustomView>

          <CustomView
            flexDirection="row"
            justifyContent="flex-end"
            gap={2}
            mt={8}
          >
            <CustomButton
              variable="default"
              onClick={resetFilters}
              style={{ marginTop: '15px' }}
            >
              Limpar Filtros
            </CustomButton>
            <CustomButton
              variable="contained"
              type="submit"
              style={{ marginTop: '15px', width: '100%', maxWidth: '192px' }}
            >
              Aplicar
            </CustomButton>
          </CustomView>
        </CustomView>
      </form>
    )
  }

  const mountData = (data: any) =>
    Array.isArray(data)
      ? data.map((row: any) => ({
          id: row?.id ?? '--',
          status: row?.statusDescricao ?? '--',
          dataCadastro: row?.dataCadastro
            ? getDateHours(row?.dataCadastro)
            : '--',
          tipo: row?.tipoIntegracaoDescricao ?? '--',
          detalheEnvio: row?.detalheEnvio ?? '--',
          observacao: row?.observacao
        }))
      : []

  const handleProccess = async () => {
    try {
      SET_LOADING(true)
      const params = {
        placas: integrationValues.placas
      }

      const res = await assetLocationServiceV2.integrateIndividualVehiclePlate(
        params
      )

      if (SUCCESS_STATUS.includes(res?.status)) {
        toast.success('Integração processada.')
        setOpenModalIntegration(false)
        recoveryIntegration(mountFilters(values))
      }
    } catch (err: any) {
      console.log('error', err)
    } finally {
      SET_LOADING(false)
    }
  }

  const handleIntegration = () => {
    setOpenModalIntegration(true)
  }

  return (
    <>
      <PageTitle>Integrações</PageTitle>
      <ModalIntegration
        isOpen={openModalIntegration}
        handleClose={() => setOpenModalIntegration(false)}
        handleConfirm={handleProccess}
        confirmDisabled={!integrationValues.tipoIntegracao}
        children={formModalIntegration()}
      />

      <CustomTable
        dataset={mountData(data?.rows)}
        columns={columnsArr}
        formFilter={formFilter()}
        handleIntegration={handleIntegration}
        handlePermissions={{ canExecuteIntegration: CAN_EXECUTE_INTEGRACOES }}
        totalCount={data?.count}
      />

      {!LOADING && (!Array.isArray(data?.rows) || data.rows.length === 0) && (
        <CustomGraphy mt="24px" fontSize="16px" textAlign="center">
          Não possui registros!
        </CustomGraphy>
      )}
    </>
  )
}
